'use client';

import { isAxiosError } from 'axios';
import { AlertCircle } from 'lucide-react';
import posthog from 'posthog-js';
import { useEffect, useState } from 'react';

import { useSignCreatorContract } from '@/api/mutations/profile.mutations';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { AnalyticsEvent } from '@/shared/lib/analytics/events';
import { captureError } from '@/shared/lib/errors/capture';
import { useContractGateStore } from '@/shared/stores/contract-gate-store';

import { ContractBody } from './ContractBody';

type DialogMode = 'contract' | 'confirm-close';

function secondsOpen(openedAt: number | null): number | null {
  return openedAt === null ? null : Math.round((Date.now() - openedAt) / 1000);
}

export function CreatorContractDialog() {
  const isOpen = useContractGateStore((s) => s.isOpen);
  const source = useContractGateStore((s) => s.source);
  const openedAt = useContractGateStore((s) => s.openedAt);
  const confirmClose = useContractGateStore((s) => s.confirmClose);
  const runPendingAndClose = useContractGateStore((s) => s.runPendingAndClose);
  const mutation = useSignCreatorContract();
  const { reset: resetMutation } = mutation;
  const [mode, setMode] = useState<DialogMode>('contract');

  useEffect(() => {
    if (!isOpen) {
      setMode('contract');
      resetMutation();
    }
  }, [isOpen, resetMutation]);

  useEffect(() => {
    if (!isOpen) return;
    posthog.capture(AnalyticsEvent.CREATOR_CONTRACT_VIEWED, { source });
  }, [isOpen, source]);

  if (!isOpen) return null;

  // X button and Escape route through the confirm prompt. Outside-click is
  // silent (accidental on scrollbar / mis-click is high; explicit intent
  // gestures only). From confirm-close mode, gestures no-op — user must
  // pick a button.
  const requestClose = () => {
    if (mode === 'contract') setMode('confirm-close');
  };
  const errorMessage = mutation.isError
    ? mutation.error instanceof Error
      ? mutation.error.message
      : 'Could not record your acceptance. Try again.'
    : null;

  const handleAccept = () => {
    mutation.mutate(undefined, {
      onSuccess: () => {
        posthog.capture(AnalyticsEvent.CREATOR_CONTRACT_ACCEPTED, {
          source,
          seconds_open: secondsOpen(openedAt),
        });
        runPendingAndClose();
      },
      onError: (error) => {
        const httpStatus = isAxiosError(error) ? (error.response?.status ?? null) : null;
        posthog.capture(AnalyticsEvent.CREATOR_CONTRACT_FAILED, {
          source,
          http_status: httpStatus,
          error_message: error instanceof Error ? error.message : String(error),
        });
        captureError(error, {
          component: 'CreatorContractDialog',
          action: 'sign-contract',
          extra: { source, http_status: httpStatus },
        });
      },
    });
  };

  const handleCloseAnyway = () => {
    posthog.capture(AnalyticsEvent.CREATOR_CONTRACT_DISMISSED, {
      source,
      seconds_open: secondsOpen(openedAt),
    });
    confirmClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(next) => (next ? undefined : requestClose())}>
      <DialogContent
        className="max-w-2xl border-0"
        showClose={mode === 'contract'}
        onEscapeKeyDown={(event) => {
          event.preventDefault();
          requestClose();
        }}
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        {mode === 'confirm-close' ? (
          <ConfirmClosePanel
            onKeepReading={() => setMode('contract')}
            onCloseAnyway={handleCloseAnyway}
          />
        ) : (
          <ContractPanel
            errorMessage={errorMessage}
            isPending={mutation.isPending}
            onAccept={handleAccept}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ContractPanel({
  errorMessage,
  isPending,
  onAccept,
}: {
  errorMessage: string | null;
  isPending: boolean;
  onAccept: () => void;
}) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>Creator Agreement</DialogTitle>
        <DialogDescription>
          Read and accept to publish, upload, or post on Taboo TV. You can keep browsing the
          platform either way.
        </DialogDescription>
      </DialogHeader>

      <div className="max-h-[55vh] overflow-y-auto px-6 py-4">
        <ContractBody />
      </div>

      {errorMessage ? (
        <div
          role="alert"
          className="mx-6 flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{errorMessage}</span>
        </div>
      ) : null}

      <DialogFooter>
        <Button
          variant="pill"
          size="pill"
          onClick={onAccept}
          disabled={isPending}
          className="shadow-none"
        >
          {isPending ? (
            <>
              <Spinner size="xs" className="text-white" />
              Recording acceptance…
            </>
          ) : (
            'I accept the Creator Agreement'
          )}
        </Button>
      </DialogFooter>
    </>
  );
}

function ConfirmClosePanel({
  onKeepReading,
  onCloseAnyway,
}: {
  onKeepReading: () => void;
  onCloseAnyway: () => void;
}) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>Are you sure?</DialogTitle>
        <DialogDescription>
          You won&apos;t be able to upload videos, post in the community, or use other creator tools
          until you accept the Creator Agreement.
        </DialogDescription>
      </DialogHeader>

      <div className="px-6 py-4 text-sm text-text-secondary">
        You can reopen this dialog any time you try a creator action.
      </div>

      <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
        <Button variant="ghost" onClick={onCloseAnyway}>
          Close anyway
        </Button>
        <Button variant="pill" size="pill" onClick={onKeepReading} className="shadow-none">
          Keep reading
        </Button>
      </DialogFooter>
    </>
  );
}
